import { App } from 'antd';
import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  PreSalesAvailabilityResult,
  PreSalesCancellationReason,
  PreSalesQuestion,
  PreSalesSubmitFormResponse,
} from '../../../dto/model';
import { scopes } from '../../../authConfig';
import { strings } from '../../../lang';
import { heatBookingPageService } from '../../../services/heat-booking-page-service';
import { GetAccessToken } from '../../../utils/auth-utils';
import { PreSalesAnswers } from '../../../utils/pre-sales-questionnaire-evaluator';
import { isUnifiedPreSalesBookingLaneEnabled, isUnifiedPreSalesProductEnabled } from '../../../utils/unified-pre-sales-pv';
import {
  resolveZeegBookingLinksInfo,
  serializePreSalesAnswers,
  serializePreSalesSubmitAnswers,
  serializePreSalesQualificationAnswers,
  ZeegBookingLinksInfo,
} from './pre-sales-questionnaire-utils';

type UsePreSalesSubmissionParams = {
  questions: PreSalesQuestion[];
  answers: PreSalesAnswers;
  crmId?: string;
  unifiedFlow?: boolean;
  isLocked?: boolean;
  onProductCancelled?: (reasons: string[]) => Promise<void>;
  signupId?: string;
  formId?: string;
  customerSource?: string;
  zipCode?: string;
  cancellationReasons: PreSalesCancellationReason[];
  /** Only a successful qualification for the current answers can bypass validation. */
  hasNoBuildableProduct?: boolean;
  isEvaluatingCancellation: boolean;
  cancellationEvaluationFailed: boolean;
  validateVisibleQuestions: () => boolean;
  prepareForSubmit?: () => Promise<void>;
  /** Called after a successful submit of a cancelled form (ReachedButNotBooked recording outcome). */
  onCancelledSubmit?: (result: PreSalesSubmitFormResponse | null) => void;
};

export const usePreSalesSubmission = ({
  questions,
  answers,
  crmId,
  unifiedFlow = false,
  isLocked = false,
  onProductCancelled,
  signupId,
  formId,
  customerSource,
  zipCode,
  cancellationReasons,
  hasNoBuildableProduct = false,
  isEvaluatingCancellation,
  cancellationEvaluationFailed,
  validateVisibleQuestions,
  prepareForSubmit,
  onCancelledSubmit,
}: UsePreSalesSubmissionParams) => {
  const { notification } = App.useApp();
  const { instance, inProgress } = useMsal();
  const [pendingCancellation, setPendingCancellation] = useState<PreSalesSubmitFormResponse | null>(null);
  const busyRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState<PreSalesSubmitFormResponse | null>(null);
  const [fieldSalesAvailability, setFieldSalesAvailability] = useState<PreSalesAvailabilityResult | null>(null);
  const [fieldSalesAvailabilityChecked, setFieldSalesAvailabilityChecked] = useState(false);
  const [zeegBookingLinksInfo, setZeegBookingLinksInfo] = useState<ZeegBookingLinksInfo | null>(null);
  const submissionSeqRef = useRef(0);
  const fieldSalesAvailabilitySeqRef = useRef(0);
  const isMountedRef = useRef(false);
  const hasHpCancellationReason = cancellationReasons.some((reason) => reason.product === 'WP');

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const reset = useCallback(() => {
    submissionSeqRef.current += 1;
    fieldSalesAvailabilitySeqRef.current += 1;
    setSubmissionResult(null);
    setPendingCancellation(null);
    setFieldSalesAvailability(null);
    setFieldSalesAvailabilityChecked(false);
    setZeegBookingLinksInfo(null);
    setIsSubmitting(false);
  }, []);

  useEffect(() => {
    reset();
    setIsSaving(false);
  }, [questions, crmId, signupId, formId, unifiedFlow, reset]);

  const save = useCallback(async () => {
    if (isLocked || busyRef.current) return;
    if (!crmId) {
      notification.error({ message: strings.preSalesQuestionnaire.saveMissingIdentifier });
      return;
    }

    setIsSaving(true);
    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const response = await heatBookingPageService.savePreSalesForm(
        {
          formIdentifier: { crmId },
          formValues: serializePreSalesAnswers(answers),
        },
        token.accessToken,
      );

      if (!response.success) {
        notification.error({
          message: strings.preSalesQuestionnaire.saveError,
          description: response.error ?? response.message,
        });
        return;
      }

      notification.success({ message: strings.preSalesQuestionnaire.saveSuccess });
    } catch (error: any) {
      notification.error({
        message: strings.preSalesQuestionnaire.saveError,
        description: error?.message,
      });
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [answers, crmId, inProgress, instance, notification, isLocked]);

  const checkFieldSalesAvailability = useCallback(async (): Promise<PreSalesAvailabilityResult> => {
    const availabilitySeq = ++fieldSalesAvailabilitySeqRef.current;
    const canApplyResult = () =>
      isMountedRef.current
      && availabilitySeq === fieldSalesAvailabilitySeqRef.current;

    if (!zipCode) {
      const result: PreSalesAvailabilityResult = { available: false };
      if (canApplyResult()) {
        setFieldSalesAvailability(result);
        setFieldSalesAvailabilityChecked(true);
      }
      return result;
    }

    try {
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const response = await heatBookingPageService.getPreSalesFieldSalesAvailability(zipCode, token.accessToken);

      if (!response.success) {
        if (canApplyResult()) {
          notification.error({
            message: strings.preSalesQuestionnaire.fieldSalesAvailabilityError,
            description: response.error ?? response.message,
          });
        }
        const result: PreSalesAvailabilityResult = { available: false };
        if (canApplyResult()) {
          setFieldSalesAvailability(result);
        }
        return result;
      }

      const result = response.data ?? { available: false };
      if (canApplyResult()) {
        setFieldSalesAvailability(result);
      }
      return result;
    } catch (error: any) {
      if (canApplyResult()) {
        notification.error({
          message: strings.preSalesQuestionnaire.fieldSalesAvailabilityError,
          description: error?.message,
        });
      }
      const result: PreSalesAvailabilityResult = { available: false };
      if (canApplyResult()) {
        setFieldSalesAvailability(result);
      }
      return result;
    } finally {
      if (canApplyResult()) {
        setFieldSalesAvailabilityChecked(true);
      }
    }
  }, [inProgress, instance, notification, zipCode]);

  const completeCancellation = useCallback(async (result: PreSalesSubmitFormResponse) => {
    await onProductCancelled?.(Object.entries(result.cancellationReasons ?? {})
      .flatMap(([product, reasons]) => reasons.map((reason) => `${product}: ${reason}`)));
    setSubmissionResult(result);
    setPendingCancellation(null);
    onCancelledSubmit?.(result);
  }, [onProductCancelled, onCancelledSubmit]);

  const submitForm = useCallback(async (cancellationConfirmed: boolean) => {
    if (isLocked || busyRef.current || pendingCancellation) return;
    if (unifiedFlow ? !signupId : !crmId) {
      notification.error({ message: strings.preSalesQuestionnaire.submitMissingIdentifier });
      return;
    }

    if (!unifiedFlow && (isEvaluatingCancellation || cancellationEvaluationFailed)) {
      return;
    }

    const requiresValidation = unifiedFlow ? !hasNoBuildableProduct : !hasHpCancellationReason;
    if (requiresValidation && !validateVisibleQuestions()) {
      notification.error({ message: strings.preSalesQuestionnaire.submitValidationError });
      return;
    }

    const submissionSeq = ++submissionSeqRef.current;
    busyRef.current = true;
    setIsSubmitting(true);
    const availabilityPromise = unifiedFlow ? null : checkFieldSalesAvailability();
    try {
      if (unifiedFlow) {
        await prepareForSubmit?.();
        if (!isMountedRef.current || submissionSeq !== submissionSeqRef.current) return;
      }
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const response = unifiedFlow
        ? await heatBookingPageService.submitUnifiedPreSalesForm({
          signupId: signupId!,
          formId,
          customerSource,
          formValues: serializePreSalesQualificationAnswers(questions, answers),
        }, token.accessToken)
        : await heatBookingPageService.submitPreSalesForm(
          {
            crmId: crmId!,
            answers: serializePreSalesSubmitAnswers(questions, answers),
            cancellation: {
              isFormCancelled: hasHpCancellationReason,
              cancellationReasons,
            },
          },
          token.accessToken,
        );

      if (!isMountedRef.current || submissionSeq !== submissionSeqRef.current) {
        return;
      }

      if (!response.success) {
        notification.error({
          message: strings.preSalesQuestionnaire.submitError,
          description: response.error ?? response.message,
        });
        return;
      }

      const result = response.data && unifiedFlow ? {
        ...response.data,
        lanePriority: response.data.lanePriority?.filter(isUnifiedPreSalesBookingLaneEnabled),
        cancellationReasons: Object.fromEntries(Object.entries(response.data.cancellationReasons ?? {})
          .filter(([product]) => isUnifiedPreSalesProductEnabled(product))),
      } : response.data;
      // A lead can be non-buildable for HP/IN yet still have an eligible lane (e.g. pv_video), so the
      // cancellation path only applies when there is no lane to book.
      const hasEligibleBookingLane = Boolean(result?.lanePriority?.length);
      if (unifiedFlow && result?.buildable === false && !hasEligibleBookingLane) {
        setPendingCancellation(result);
        if (cancellationConfirmed) {
          await completeCancellation(result);
        }
        return;
      }
      setSubmissionResult(result ?? null);
      notification.success({ message: strings.preSalesQuestionnaire.submitSuccess });

      // A submitted cancellation is a "reached but not booked" outcome — signal the recording layer.
      if (unifiedFlow ? response.data?.buildable === false && !hasEligibleBookingLane : hasHpCancellationReason) {
        onCancelledSubmit?.(response.data ?? null);
      }

      const linksInfo = resolveZeegBookingLinksInfo(response.data?.bookingLinks);
      if (linksInfo) {
        setZeegBookingLinksInfo(linksInfo);
      }

      if (unifiedFlow) {
        setFieldSalesAvailabilityChecked(true);
        return;
      }

      const availabilityResult = await availabilityPromise;
      if (!isMountedRef.current || submissionSeq !== submissionSeqRef.current) {
        return;
      }

      const isFieldSalesEligible = Boolean(response.data?.fieldSalesBookingAllowed && availabilityResult?.available);
      const eligibilityResponse = await heatBookingPageService.updatePreSalesFieldSalesEligibility(
        { identifier: { crmId: crmId! }, isFieldSalesEligible },
        token.accessToken,
      );
      if (!eligibilityResponse.success) {
        console.error(
          'Failed to report field sales eligibility',
          eligibilityResponse.error ?? eligibilityResponse.message,
        );
      }
    } catch (error: any) {
      if (submissionSeq === submissionSeqRef.current) {
        notification.error({
          message: strings.preSalesQuestionnaire.submitError,
          description: error?.message,
        });
      }
    } finally {
      busyRef.current = false;
      if (isMountedRef.current && submissionSeq === submissionSeqRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [
    answers,
    isLocked,
    pendingCancellation,
    cancellationEvaluationFailed,
    cancellationReasons,
    checkFieldSalesAvailability,
    crmId,
    unifiedFlow,
    signupId,
    formId,
    customerSource,
    hasHpCancellationReason,
    hasNoBuildableProduct,
    inProgress,
    instance,
    isEvaluatingCancellation,
    notification,
    onCancelledSubmit,
    questions,
    validateVisibleQuestions,
    prepareForSubmit,
    completeCancellation,
  ]);

  const submit = useCallback(() => submitForm(false), [submitForm]);

  const confirmCancellation = async () => {
    if (isLocked || busyRef.current) return;
    if (!pendingCancellation) {
      if (unifiedFlow && hasNoBuildableProduct && !isEvaluatingCancellation && !cancellationEvaluationFailed) {
        await submitForm(true);
      }
      return;
    }
    busyRef.current = true;
    setIsSubmitting(true);
    try {
      await completeCancellation(pendingCancellation);
    } catch (error: unknown) {
      notification.error({ message: strings.preSalesQuestionnaire.submitError,
        description: error instanceof Error ? error.message : undefined });
    } finally {
      busyRef.current = false;
      setIsSubmitting(false);
    }
  };

  return {
    pendingCancellation,
    confirmCancellation,
    dismissCancellation: () => { if (!busyRef.current) setPendingCancellation(null); },
    isSaving,
    isSubmitting,
    submissionResult,
    fieldSalesAvailability,
    fieldSalesAvailabilityChecked,
    zeegBookingLinksInfo,
    save,
    submit,
    reset,
  };
};
